// BarcodeScannerModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  buildBarcodeDecodeHints,
  BARCODE_SCAN_FORMATS,
  isIgnorableBarcodeScanError,
  normalizeBarcodeValue,
  resolveBarcodeCameraError,
  resolvePreferredVideoDeviceId,
} from 'utilities/Barcode';

// CSS
import './BarcodeScannerModal.css';

// Components
import { BrowserMultiFormatReader } from '@zxing/library/esm/browser/BrowserMultiFormatReader';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';

const EMPTY_CAMERA_OPTIONS = [];

/**
 * BarcodeScannerModal
 *
 * Displays a camera scanner for detecting a barcode and returning its string value.
 *
 * @param {boolean} isOpen - Whether the modal is visible.
 * @param {array} decodeFormats - ZXing barcode formats to scan for.
 * @param {string} label - Optional label for the modal title.
 * @param {function} onDetected - Called with the scanned barcode value.
 * @param {string} scanTargetLabel - Lowercase description of the scannable target.
 * @param {function} setOpen - Updates the modal open state.
 *
 * @returns {ReactNode} The rendered barcode scanner modal.
 */
const BarcodeScannerModal = ({
  isOpen,
  decodeFormats = BARCODE_SCAN_FORMATS,
  label,
  onDetected,
  scanTargetLabel = 'barcode',
  setOpen,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [cameraDevices, setCameraDevices] = useState(EMPTY_CAMERA_OPTIONS);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [scannerError, setScannerError] = useState('');
  const [statusText, setStatusText] = useState('Point the camera at a barcode.');

  // ----- REFS ----- //

  const videoRef = useRef(null);
  const readerRef = useRef(null);

  // ----- MEMOS ----- //

  const modalTitle = useMemo(() => {
    const trimmedLabel = String(label || '').trim();
    return trimmedLabel ? `Scan ${trimmedLabel}` : 'Scan Barcode';
  }, [label]);

  const cameraOptions = useMemo(() => {
    return (Array.isArray(cameraDevices) ? cameraDevices : []).map((device, index) => ({
      label: String(device?.label || '').trim() || `Camera ${index + 1}`,
      value: String(device?.deviceId || '').trim(),
    }));
  }, [cameraDevices]);

  const decodeHints = useMemo(() => buildBarcodeDecodeHints(decodeFormats), [decodeFormats]);

  const defaultStatusText = useMemo(() => {
    const targetLabel = String(scanTargetLabel || '').trim() || 'barcode';
    return `Point the camera at the ${targetLabel}.`;
  }, [scanTargetLabel]);

  const stopScanner = useCallback(() => {
    if (readerRef.current) {
      readerRef.current.reset();
      readerRef.current = null;
    }
  }, []);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen) {
      stopScanner();
      setScannerError('');
      setStatusText(defaultStatusText);
      return undefined;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setScannerError('Camera scanning is not supported in this browser.');
      return undefined;
    }

    let cancelled = false;
    const reader = new BrowserMultiFormatReader(decodeHints, 250);

    const loadCameraDevices = async () => {
      try {
        const videoDevices = await reader.listVideoInputDevices();
        if (cancelled) return;

        setCameraDevices(videoDevices);
        setSelectedDeviceId((current) => resolvePreferredVideoDeviceId(videoDevices, current));
      } catch (error) {
        if (cancelled) return;
        setScannerError(resolveBarcodeCameraError(error));
      } finally {
        reader.reset();
      }
    };

    loadCameraDevices();

    return () => {
      cancelled = true;
      reader.reset();
    };
  }, [decodeHints, defaultStatusText, isOpen, stopScanner]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!videoRef.current) return undefined;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return undefined;

    let cancelled = false;
    const reader = new BrowserMultiFormatReader(decodeHints, 250);
    readerRef.current = reader;
    setScannerError('');
    setStatusText(defaultStatusText);

    reader.decodeFromVideoDevice(selectedDeviceId || null, videoRef.current, (result, error) => {
      if (cancelled) return;

      if (result) {
        const scannedValue = normalizeBarcodeValue(result.getText());
        if (!scannedValue) return;

        stopScanner();
        if (typeof onDetected === 'function') {
          onDetected(scannedValue);
        }
        setOpen(false);
        return;
      }

      if (error && !isIgnorableBarcodeScanError(error)) {
        console.error('BarcodeScannerModal: scan loop error.', error);
      }
    })
      .then(() => {
        if (cancelled) return;
        setStatusText(defaultStatusText);
      })
      .catch((error) => {
        if (cancelled) return;
        setScannerError(resolveBarcodeCameraError(error));
        setStatusText('');
        stopScanner();
      });

    return () => {
      cancelled = true;
      if (readerRef.current === reader) {
        readerRef.current = null;
      }
      reader.reset();
    };
  }, [decodeHints, defaultStatusText, isOpen, onDetected, selectedDeviceId, setOpen, stopScanner]);

  // ----- HANDLERS ----- //

  /**
 * Handles close.
 */

  const handleClose = useCallback(() => {
    stopScanner();
    setOpen(false);
  }, [setOpen, stopScanner]);

  // ----- RENDER ----- //

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      width={isMobile ? '100%' : 560}
      height={isMobile ? '100%' : 640}
      onClose={handleClose}
    >
      <ModalForm>
      <div className="barcode-scanner-modal-container">

        {cameraOptions.length > 1 ? (

          <div className="barcode-scanner-modal-field">

            {/* LABEL */}
            <div
              className="barcode-scanner-modal-label"
            >
              Camera
            </div>

            <SelectInput
              hybrid
              value={selectedDeviceId}
              options={cameraOptions}
              onChange={(event) => setSelectedDeviceId(event.target.value)}
            />
          </div>
        ) : null}

        {/* VIDEO SHELL */}
        <div
          className="barcode-scanner-modal-video-shell"
        >

          {/* VIDEO */}
          <video
            className="barcode-scanner-modal-video"
            ref={videoRef}
            autoPlay
            muted
            playsInline
          />
        </div>

        {/* STATUS */}
        <div
          className="barcode-scanner-modal-status"
          style={{
            color: scannerError ? theme.red : theme.foregroundMuted,
          }}
        >
          {scannerError || statusText || 'Align the barcode inside the camera frame.'}
        </div>
      </div>
      </ModalForm>

      <ModalButtons>
        <Button
          label="Close"
          onClick={handleClose}
        />
      </ModalButtons>
    </Modal>
  );
};

// Dev Tools
BarcodeScannerModal.displayName = 'BarcodeScannerModal';

export default BarcodeScannerModal;
